/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-2016 Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import Dropzone from 'react-dropzone';
import Avatar from 'react-avatar';
import { connect } from 'react-redux';
import request from 'superagent';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import Auth from '../Auth';
import Form from '../../../../../components/Form';
import Hidden from '../../../../../components/Form/Input/Hidden';
import Link from '../../../../../components/Link';
import s from './Avatar.scss';
import * as SessionActions from '../../../../../actions/SessionActions';
import history from '../../../../../history';

const messages = defineMessages({
  personalizeProfile: {
    id: 'auth.avatar.personalizeProfile',
    defaultMessage: 'Let’s personalize your profile.',
    description: 'Let’s personalize your profile',
  },
  selectImage: {
    id: 'auth.avatar.selectImage',
    defaultMessage:
      'Select an image or a logo of your Salon to upload from your computer',
    description: 'Select Image Message',
  },
  uploadImage: {
    id: 'auth.avatar.uploadImage',
    defaultMessage: 'Upload an image',
    description: 'Upload an image Message',
  },
  step5: {
    id: 'auth.avatar.step5',
    defaultMessage: 'STEP 5 OF 5',
    description: 'step5',
  },
  btn1: {
    id: 'auth.avatar.btn1',
    defaultMessage: '...submitting',
    description: 'btn1',
  },
  btn2: {
    id: 'auth.avatar.btn2',
    defaultMessage: 'COMPLETE SIGN UP',
    description: 'btn2',
  },
});

class AvatarUpload extends Component {
  static contextTypes = {
    routeParams: PropTypes.object.isRequired,
    fetch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
  };

  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  constructor(...args) {
    super(...args);
    this.onDrop = this.onDrop.bind(this);
    this.onUploadClick = this.onUploadClick.bind(this);
    this.onValidSubmit = this.onValidSubmit.bind(this);
    this.state = {
      uploadError: false,
      isUploading: false,
      transferDirection: null,
      uploadPercent: null,
      submitting: false,
      submitSuccess: false,
      submitError: false,
      value: {},
    };
  }

  onDrop(acceptedFiles, rejectedFiles) {
    const { siteId } = this.context.routeParams;
    if (rejectedFiles.length > 0) {
      this.setState({ uploadError: true });
    }
    if (acceptedFiles.length > 0) {
      this.setState({
        isUploading: true,
        uploadError: false,
        transferDirection: 'upload',
        uploadPercent: 0,
      });
      // Avatars are rendered as plain <img> tags on clubh.ca (a different
      // registrable domain), which can never carry the auth cookie/JWT, so
      // they must be publicly fetchable via GET /:siteId/asset/:id.
      const req = request.post(`/${siteId}/upload?visibility=public`);
      acceptedFiles.forEach(file => {
        req.attach('myfiles', file, file.name);
      });
      req
        .on('progress', event => {
          this.setState({
            transferDirection: event.direction,
            uploadPercent: event.percent,
          });
        })
        .end((err, res) => {
          this.setState({ isUploading: false });
          if (res.body instanceof Object) {
            this.setState({
              value: {
                avatar: { media: { _id: res.body._id } },
                focalSelector: '',
              },
            });
          }
        });
    }
  }

  async onValidSubmit(e) {
    const { dispatch } = this.props;
    const { siteId } = this.context.routeParams;
    this.setState({
      submitting: true,
      submitSuccess: false,
      submitError: false,
    });
    const resp = await this.context.fetch(`/${siteId}/auth/register/avatar`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(e.value),
      credentials: 'include',
    });
    const data = await resp.json();
    if (!data || resp.status !== 200 || data.responseCode === 1) {
      this.setState({
        submitError: true,
        submitErrorMessage: data.responseDesc,
        submitting: false,
      });
    } else {
      dispatch(SessionActions.setMeData(data.me));
      this.setState(
        {
          submitting: false,
          submitError: false,
          submitSuccess: true,
          value: {},
        },
        () => history.push(`/${siteId}/auth/complete`),
      );
    }
  }

  onUploadClick() {
    this.dropzone.open();
  }

  render() {
    const { title, session } = this.props;
    const { intl } = this.context;
    const { siteId } = this.context.routeParams;
    const userData = session.USER_DATA;
    let avatarSrc;
    if ('avatar' in this.state.value) {
      avatarSrc = `/${siteId}/asset/${this.state.value.avatar.media._id}?width=125&height=125&type=fill`;
    } else if (userData.avatar != null && 'media' in userData.avatar) {
      avatarSrc = `/${siteId}/asset/${userData.avatar.media._id}?width=125&height=125&type=fill`;
    } else {
      avatarSrc = '/images/salon-avatar.svg';
    }

    return (
      <Auth>
        <div className="auth__form">
          <div className="auth__step">
            <FormattedMessage {...messages.step5} />
          </div>
          <div className="auth__step-info">
            <FormattedMessage {...messages.personalizeProfile} />
          </div>

          <div className="auth__main">
            <Form
              onValidSubmit={this.onValidSubmit}
              title={title}
              value={this.state.value}
              isSubmitting={this.state.submitting}
              submitButtonLabel={
                this.state.submitting
                  ? intl.formatMessage(messages.btn1)
                  : intl.formatMessage(messages.btn2)
              }
            >
              <div className="avatar">
                <Dropzone
                  ref={node => {
                    this.dropzone = node;
                  }}
                  style={{ border: 'none', pointer: 'cursor' }}
                  // className={`images__upload ${isUploadingClass}`}
                  disableClick={false}
                  accept="image/*"
                  multiple={false}
                  // minSize={this.props.minSize}
                  // maxSize={this.props.maxSize}
                  onDrop={this.onDrop}
                >
                  <Avatar
                    size={115}
                    round
                    className="avatar__image"
                    src={avatarSrc}
                  />
                </Dropzone>
                <div className="avatar__upload">
                  <p className="avatar__text">
                    <FormattedMessage {...messages.selectImage} />
                  </p>
                  <input
                    className="avatar__upload-input js-upload"
                    type="file"
                    name="file-upload-field"
                    value=""
                  />
                  <a
                    onClick={this.onUploadClick}
                    className="avatar__upload-link"
                  >
                    <FormattedMessage {...messages.uploadImage} />
                  </a>
                </div>
              </div>
              <Link
                className="form-m__btn form-m__btn--skip avatar__skip"
                to={`/${siteId}/`}
              >
                SKIP STEP
              </Link>
              <Hidden name="avatar.media._id" required />
              <Hidden name="avatar.focalSelector" />
            </Form>

            <Link
              className="form-m__btn form-m__btn--skip avatar__skip"
              to={`/${siteId}/`}
            >
              SKIP STEP
            </Link>
          </div>
        </div>
      </Auth>
    );
  }
}

export default connect(state => ({ session: state.session }))(
  withStyles(s)(AvatarUpload),
);



// WEBPACK FOOTER //
// ./src/routes/client/site/auth/avatar/AvatarUpload.js